import {
  findShippingRegion,
  SHIPPING_ADDRESS_MAX_LENGTHS,
  SHIPPING_REGION_LABELS,
} from '@repo/api-contracts';
import { Button, cn, FormField, Input, Label } from '@repo/ui';
import { type ReactElement, useId, useState } from 'react';
import { openPostcode } from '../lib/kakao-postcode';

interface PostcodeValue {
  readonly postalCode: string;
  readonly address1: string;
}

export interface PostcodeSearchFieldsProps {
  readonly value: PostcodeValue;
  /** 바뀐 칸만 넘긴다 — 호출부가 이전 값과 병합한다. */
  readonly onChange: (patch: Partial<{ postalCode: string; address1: string }>) => void;
  readonly errors?: Partial<Record<'postalCode' | 'address1', string>>;
  /** 저장 중 등 — 칸과 주소 검색을 잠근다. */
  readonly disabled?: boolean;
}

/** 로더·SDK 오류 문구는 보여 주지 않는다 — 다음에 할 일(직접 입력)만 안내한다. */
const SEARCH_FAILURE_MESSAGE = '주소 검색을 불러오지 못했습니다. 주소를 직접 입력할 수 있습니다.';

/** 칸 높이 44px — 휴대폰에서도 누르기 쉬운 크기. 주소 검색 버튼도 같은 높이다. */
const INPUT_CLASS = 'h-11';

/** 배송지 우편번호는 5자리 숫자다(SUPER-86 — 지역 판별, `postalCodeSchema`). */
const POSTAL_CODE_MAX_LENGTH = 5;

/**
 * 우편번호(주소 검색 버튼)·기본 주소 두 칸 — 배송지 입력(`ShippingAddressFields`)과 비회원 견적 요청 배송지 카드(spec D8)가 함께 쓴다.
 * 카카오 우편번호 검색(`openPostcode`, 15초 타임아웃)으로 두 칸을 채우고, 선택 없이 닫으면 아무것도 덮어쓰지 않으며, 창이 열리면
 * 잠금을 풀어 응답 없는 창이 입력을 막지 않고, 불러오지 못하면 직접 입력으로 이어 간다.
 * 두 칸은 읽기 전용이다 — 주소 검색으로만 채우고, 검색을 불러오지 못한 뒤(`searchError`)에만 직접 고칠 수 있다. 주소만 고쳐
 * 우편번호(지역 배송비 기준)와 다른 지역 주소를 넣지 못하게 하려는 것이다(SUPER-86). 우편번호 옆에 판별한 지역을 보인다.
 * 부모 그리드(`sm:grid-cols-2`)의 자식으로 바로 놓이도록 감싸는 요소 없이(Fragment) 돌려준다 — 두 칸 모두 두 열을 차지한다.
 */
export const PostcodeSearchFields = ({
  value,
  onChange,
  errors = {},
  disabled = false,
}: PostcodeSearchFieldsProps): ReactElement => {
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const postalCodeId = useId();
  const postalCodeErrorId = `${postalCodeId}-error`;
  /** 검색을 쓸 수 없을 때만 직접 입력한다. */
  const isManual = searchError !== null;
  const region = findShippingRegion(value.postalCode.trim());
  const regionLabel = region === null ? '-' : SHIPPING_REGION_LABELS[region];

  const handleSearch = async (): Promise<void> => {
    setSearchError(null);
    setIsSearching(true);

    try {
      // 창이 열리면 잠금을 푼다 — 팝업이 응답하지 않아도(차단·방치) 버튼이 영구히 잠기지 않는다.
      const result = await openPostcode({ onOpen: () => setIsSearching(false) });

      if (result !== null) {
        onChange({ postalCode: result.postalCode, address1: result.address1 });
      }
    } catch {
      setSearchError(SEARCH_FAILURE_MESSAGE);
    } finally {
      setIsSearching(false);
    }
  };

  return (
    <>
      {/* 우편번호 칸과 주소 검색 버튼을 한 줄에 같은 높이로 세우고, 오류는 그 줄 아래에 둔다. */}
      <div className="flex flex-col gap-1.5 sm:col-span-2">
        <Label htmlFor={postalCodeId} isRequired>
          우편번호
        </Label>
        <div className="flex gap-2 sm:max-w-sm">
          <Input
            id={postalCodeId}
            autoComplete="postal-code"
            inputMode="numeric"
            required
            readOnly={!isManual}
            disabled={disabled}
            maxLength={POSTAL_CODE_MAX_LENGTH}
            className={cn(INPUT_CLASS, 'min-w-0 flex-1 tabular-nums', !isManual && 'bg-muted/40')}
            hasError={Boolean(errors.postalCode)}
            aria-describedby={errors.postalCode ? postalCodeErrorId : undefined}
            value={value.postalCode}
            onChange={(event) => onChange({ postalCode: event.target.value })}
          />
          <Button
            type="button"
            variant="outline"
            className="h-11 shrink-0 border-foreground/20 px-5"
            disabled={disabled}
            isLoading={isSearching}
            onClick={() => {
              void handleSearch();
            }}
          >
            주소 검색
          </Button>
          <span className="shrink-0 self-center whitespace-nowrap text-sm text-muted-foreground">
            지역: {regionLabel}
          </span>
        </div>
        {errors.postalCode ? (
          <p id={postalCodeErrorId} role="alert" className="text-xs text-destructive">
            {errors.postalCode}
          </p>
        ) : null}
      </div>

      {searchError ? (
        <p
          role="alert"
          className="border-l-2 border-destructive bg-destructive/5 px-4 py-3 text-sm text-destructive sm:col-span-2"
        >
          {searchError}
        </p>
      ) : null}

      <FormField
        label="주소"
        autoComplete="address-line1"
        isRequired
        required
        readOnly={!isManual}
        disabled={disabled}
        maxLength={SHIPPING_ADDRESS_MAX_LENGTHS.address1}
        className={cn(INPUT_CLASS, !isManual && 'bg-muted/40')}
        containerClassName="sm:col-span-2"
        value={value.address1}
        error={errors.address1}
        onChange={(event) => onChange({ address1: event.target.value })}
      />
    </>
  );
};
